import {RouterLinkTarget} from '@aboutyou/router';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';
import {tabletAndUp} from '../../../../constants/customMedia';
import {FONT_WEIGHTS} from '../../../../constants/typography';
import {ImageType} from '../../../../types/Image';
import {Image} from '../../../atoms/Image/Image';
import {PrimaryResizeSide, Ratio} from '../../../atoms/Image/types';
import {UnstyledLink} from '../../../atoms/UnstyledLink/UnstyledLink';
import {
  ContentBlockCTA,
  ContentBlockCTAProps,
} from '../ContentBlockCTA/ContentBlockCTA';
import {
  ContentBlockText,
  ContentBlockTextProps,
} from '../ContentBlockText/ContentBlockText';

export interface ContentTeaserTileProps {
  image: ImageType & {
    aspectRatio: Ratio;
    primaryResizeSide?: PrimaryResizeSide;
  };
  importance?: 'high' | 'low' | 'auto';
  content?: {
    headlines?: ContentBlockTextProps;
    sublines?: ContentBlockTextProps;
    logo?: ImageType & {aspectRatio: Ratio};
    ctas?: ContentBlockCTAProps[];
    justifyContent?: 'center' | 'space-between';
  };
  href?: string;
  testId?: string;
  className?: string;
  onClickCTA?: VoidFunction;
}

export const ContentBlockImageTile: FC<ContentTeaserTileProps> = ({
  testId = 'contentBlockImageTile',
  href,
  image,
  content,
  className,
  importance = 'auto',
  onClickCTA,
}) => {
  const contentGap = content?.justifyContent === 'center' ? 0 : 1.5;

  return (
    <Wrapper className={className} data-testid={testId}>
      <StyledWrapper
        as={href ? StyledLink : 'div'}
        to={href}
        data-testid={`${testId}Link`}
      >
        <BackgroundWrapper className="imageBackgroundWrapper">
          <StyledImage
            alt={image.description}
            breakpoints={breakpoints}
            ratio={image.aspectRatio}
            src={image.url}
            loading={importance === 'high' ? 'eager' : 'lazy'}
            widths={possibleImageWidths}
            data-testid={`${testId}Background`}
            primaryResizeSide={image.primaryResizeSide}
          />
        </BackgroundWrapper>

        {content &&
        Boolean(
          content.headlines ||
            content.sublines ||
            content.ctas?.length ||
            content.logo,
        ) ? (
          <TeaserTextWrapper $contentGap={contentGap}>
            {content.logo && (
              <StyledCenteredImage
                src={content.logo.url}
                alt={content.logo.description}
                breakpoints={[]}
                ratio={content.logo.aspectRatio}
                widths={possibleLogoWidths}
                data-testid={`${testId}Logo`}
              />
            )}

            {content.headlines && content.headlines.text !== '' && (
              <Headline testId={`${testId}Headline`} {...content.headlines} />
            )}

            {content.sublines && content.sublines.text !== '' && (
              <ContentBlockText
                testId={`${testId}Subline`}
                {...content.sublines}
              />
            )}

            {content.ctas?.length && (
              <CTAWrapper
                pushToTheBottom={Boolean(
                  content.logo ||
                    (content.headlines && content.headlines?.text !== '') ||
                    (content.sublines && content.sublines?.text !== ''),
                )}
              >
                {content.ctas.map(
                  (cta, index) =>
                    cta.text.text !== '' && (
                      <ContentBlockCTA
                        key={index}
                        data-testid={`${testId}CTA`}
                        onClick={onClickCTA}
                        {...cta}
                      />
                    ),
                )}
              </CTAWrapper>
            )}
          </TeaserTextWrapper>
        ) : null}
      </StyledWrapper>
    </Wrapper>
  );
};

const breakpoints = [
  {
    breakpoint: '(max-width: 767px)',
    columns: 2,
  },
  {
    breakpoint: '(max-width: 1023px)',
    columns: 3,
  },
  {
    columns: 1,
  },
];

const possibleImageWidths = [400, 600, 800, 960, 1200, 1400];
const possibleLogoWidths = [200, 400, 600];

const Wrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;

const StyledWrapper = styled.div<{to?: RouterLinkTarget}>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`;

const StyledLink = styled(UnstyledLink)`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`;

const BackgroundWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  width: 100%;
`;

const StyledImage = styled(Image)`
  height: 100%;
  object-fit: cover;
  object-position: center top;
  width: 100%;
`;

const CTAWrapper = styled.div<{
  pushToTheBottom: boolean;
}>`
  display: flex;
  gap: 1rem;

  ${({pushToTheBottom}) =>
    pushToTheBottom &&
    tabletAndUp(css`
      bottom: 2.5rem;
      position: absolute;
    `)}
`;

export const TeaserTextWrapper = styled.div<{$contentGap: number}>`
  gap: ${({$contentGap}) => `${$contentGap}rem`};
  position: absolute;
  overflow: hidden;
  top: 0;
  color: initial;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.33));
`;

const Headline = styled(ContentBlockText)`
  margin-top: 0;
  word-break: break-word;
  font-weight: ${FONT_WEIGHTS.bold};
  max-width: 80%;

  ${tabletAndUp(css`
    margin-bottom: 0;
  `)}
`;

export const StyledCenteredImage = styled(Image)`
  object-fit: contain;
  max-width: 150px;
  max-height: 120px;
`;
